/* eslint max-lines: ["error", {max: 270}] -- a long file */
import { useRef } from "react";

import { differenceInCalendarDays } from "date-fns";
import isNil from "lodash/isNil";

import {
  AnalyticsFieldName,
  HomeAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { TabSources } from "@/constants/analytics/Tabs";
import { ModalPath } from "@/constants/Navigation";
import { HomeTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import useCarouselUserChange from "@/hooks/useCarouselUserChange";
import useScheduleEvents from "@/hooks/useScheduleEvents";
import { useAnalytics } from "@/services/Analytics";
import {
  useAppUserProxyPatientsSelector,
  useAppUserSelector,
} from "@/store/reducers/appUser";
import { useScheduleEventDetailsModalDispatch } from "@/store/reducers/modals/scheduleEventDetailsModal";
import type { ScheduleEventDetailsData } from "@/store/reducers/modals/scheduleEventDetailsModal";
import EmptyUpcomingEventsCard from "@/ui/components/home/EmptyUpcomingEventsCard";
import UpcomingEventCard from "@/ui/components/home/UpcomingEventCard";
import { SectionHeader } from "@/ui/core";
import { Box, CarouselWrapper, FlatList } from "@/ui/layout";
import type { FlashListRef } from "@/ui/layout/list/FlatList";
import { getRelativeDay, getTimeRange } from "@/utils/date.util";
import {
  getAppointmentDetailResponseFromScheduleEvent,
  getScheduleEventProps,
} from "@/utils/schedule.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import { joinTestIDs } from "@/utils/string.util";
import { getTabSelectorOptions } from "@/utils/user.util";

const TwoWeekDaysCutoff = 14;
const EventCardCount = 7;

interface TrackPressProps {
  externalId: string;
  isGroupClassEvent?: boolean;
  title: string;
}

interface RenderUpcomingEventProps {
  selectedUserId: string;
  setModalData: (data: ScheduleEventDetailsData) => void;
  trackPress: ({
    externalId,
    isGroupClassEvent,
    title,
  }: TrackPressProps) => void;
}

const renderUpcomingEvent = ({
  selectedUserId,
  setModalData,
  trackPress,
}: RenderUpcomingEventProps) => {
  // eslint-disable-next-line react/display-name -- this is not a proper Component, only a render function
  return ({ item }: { item: ScheduledEventResponseItem }) => {
    const {
      attendees,
      description,
      endDate,
      externalId,
      frequency,
      groupClassExternalId,
      groupSectionId,
      iconName,
      isGroupClassEvent,
      isPhoneCallEvent,
      isRecurring,
      selectedUserId: eventPatientExternalId,
      startDate,
      title,
      typeGroup,
    } = getScheduleEventProps({
      event: item,
      isGroupClassEvent: item.isGroupClassEvent,
    });

    const timeRange = getTimeRange({
      endDate,
      shouldIncludeStartAmPm: false,
      startDate,
    });

    const whichDay = getRelativeDay(startDate);
    const when = `${whichDay}, ${timeRange}`;

    const appointmentMetaData =
      getAppointmentDetailResponseFromScheduleEvent(item);

    const onPress = () => {
      trackPress({ externalId, isGroupClassEvent, title });
      const patientExternalIdForEventDetails = isNotNilNotEmpty(
        eventPatientExternalId,
      )
        ? eventPatientExternalId
        : selectedUserId;
      setModalData({
        appointmentMetaData,
        eventDetails: {
          attendees,
          description,
          endDate,
          externalId,
          frequency,
          groupClassExternalId,
          groupSectionId,
          iconName,
          isGroupClassEvent,
          isPhoneCallEvent,
          isRecurring,
          patientExternalId: patientExternalIdForEventDetails,
          startDate,
          title,
          typeGroup: typeGroup || "THERAPY",
        },
        isUpcomingEvent: true,
        selectedUserId,
        title: isGroupClassEvent ? "Event details" : "Appointment details",
      });

      router.navigate(ModalPath.ScheduleEventDetails);
    };

    return (
      <UpcomingEventCard
        attendees={attendees}
        onPress={onPress}
        testID={joinTestIDs(HomeTestID.UpcomingEventsCard, externalId)}
        title={title}
        when={when}
      />
    );
  };
};

const UpcomingEventsSection = () => {
  const { track } = useAnalytics();
  const { setScheduleEventDetailsData: setModalData } =
    useScheduleEventDetailsModalDispatch();
  const {
    externalId: appUserId,
    isPatient: isAppUserPatient,
    isSupport: isAppUserSupport,
  } = useAppUserSelector();
  const proxyPatients = useAppUserProxyPatientsSelector();

  const flatListRef = useRef<FlashListRef<ScheduledEventResponseItem> | null>(
    null,
  );

  const { handleUserChange, selectedUserId } =
    useCarouselUserChange<ScheduledEventResponseItem>({
      analyticsComponent: UpcomingEventsSection.name,
      analyticsSource: TabSources.Home,
      scrollableRef: flatListRef,
    });

  const { isLoading, refetch, scheduleEvents } = useScheduleEvents({
    isPastEvents: false,
    isPatient: isAppUserPatient,
    patientExternalId: selectedUserId,
  });

  const ungroupedEvents = isNil(scheduleEvents)
    ? []
    : scheduleEvents.flatMap((group) => group.data);
  const eventsHappeningSoon = ungroupedEvents.filter((event) => {
    const { isGroupClassEvent } = event;
    const { startDate } = getScheduleEventProps({
      event,
      isGroupClassEvent,
    });
    const laterDate = new Date(startDate);
    const now = new Date();
    return differenceInCalendarDays(laterDate, now) <= TwoWeekDaysCutoff;
  });
  const userTabs = getTabSelectorOptions({
    appUserCount: 0,
    appUserId,
    isAppUserPatient,
    patients: proxyPatients,
    shouldIncludeSelf: true,
  });

  const trackPress = ({
    externalId,
    isGroupClassEvent,
    title,
  }: TrackPressProps) => {
    track(
      isGroupClassEvent
        ? HomeAnalyticsEvent.UpcomingEventCardPressed
        : HomeAnalyticsEvent.UpcomingAppointmentCardPressed,
      {
        [AnalyticsFieldName.Component]: UpcomingEventCard.name,
        [AnalyticsFieldName.EventId]: externalId,
        [AnalyticsFieldName.EventTitle]: title,
        [AnalyticsFieldName.SelectedUser]: selectedUserId,
        [AnalyticsFieldName.Source]: UpcomingEventsSection.name,
      },
    );
  };

  return (
    <CarouselWrapper
      hasPaddingTop={false}
      header={
        <SectionHeader
          appUserId={appUserId}
          isAppUserSupport={isAppUserSupport}
          isInlineHeader
          onUserChange={handleUserChange}
          testID={HomeTestID.UpcomingEventsHeader}
          title="Coming up"
          userTabs={userTabs}
        />
      }
    >
      {eventsHappeningSoon.length === 0 ? (
        <Box paddingHorizontal={24}>
          <EmptyUpcomingEventsCard testID={HomeTestID.EmptyUpcomingEvents} />
        </Box>
      ) : (
        <FlatList<ScheduledEventResponseItem>
          data={eventsHappeningSoon.slice(0, EventCardCount)}
          isDataLoading={isLoading}
          isHorizontal
          keyExtractor={(item) =>
            getScheduleEventProps({
              event: item,
              isGroupClassEvent: item.isGroupClassEvent,
            }).externalId
          }
          listRef={flatListRef}
          onRefresh={() => void refetch()}
          renderItem={renderUpcomingEvent({
            selectedUserId,
            setModalData,
            trackPress,
          })}
          shouldRemoveContentVerticalPadding
          testID={HomeTestID.UpcomingEventsList}
        />
      )}
    </CarouselWrapper>
  );
};

export default UpcomingEventsSection;
